import type { FC } from 'react';
import { useEffect } from 'react';

import type { CoreStoreType } from '../../store/CoreStore';
import { useConfigSlice } from '../../store/slices/config/selectors';
import { useGameStateSlice } from '../../store/slices/game/selectors';
import { useSettingsSlice } from '../../store/slices/settings/selectors';
import { useSubtitlesSlice } from '../../store/slices/subtitles/selectors';

import { ChatCommunicationController } from './chatCommunicationController';
import type { IChatCommunicationProcessor } from './types';

export const chatCommunicationController = new ChatCommunicationController();

interface ChatCommunicationControllerConnectorProps {
  gameStore: CoreStoreType;
  processors?: IChatCommunicationProcessor[];
}

export const ChatCommunicationControllerConnector: FC<ChatCommunicationControllerConnectorProps> = ({
  gameStore,
  processors,
}): null => {
  const chatWS = useConfigSlice.env()?.chatWS;
  const JSESSIONID = useConfigSlice.JSESSIONID();
  const userId = useConfigSlice.playerConfig()?.userId;
  const tableId = useConfigSlice.tableId();
  const casinoId = useConfigSlice.casinoConfig()?.casinoId;
  const tableName = useConfigSlice.tableConfig()?.tableName;
  const showOtherMessages = useConfigSlice.showOtherMessages();
  const isSubtitlesAvailable = useConfigSlice.isSubtitlesAvailable();
  const isSubtitlesEnabled = useSubtitlesSlice.getIsEnabled();
  const subtitlesEnabled = isSubtitlesAvailable && isSubtitlesEnabled;
  const isGamePaused = useGameStateSlice.getIsGamePaused();
  const isGameAbandoned = useGameStateSlice.getIsGameAbandoned();
  const lang = useSettingsSlice.currentLanguage();
  const subLang = useSubtitlesSlice.getLang(lang);
  const isChatEnabled = !useConfigSlice.isChatDisabled();

  useEffect(() => {
    if (processors) {
      processors.forEach((processor) => chatCommunicationController.setProcessor(processor));
    }

    chatCommunicationController.setStore(gameStore);
  }, [processors, gameStore]);

  useEffect(() => {
    if (JSESSIONID && tableId && userId && chatWS && casinoId && tableName) {
      if (isChatEnabled || subtitlesEnabled) {
        chatCommunicationController.connect(
          chatWS,
          JSESSIONID,
          userId,
          tableId,
          casinoId,
          tableName,
          `${userId}_${Date.now()}`,
          showOtherMessages,
          subtitlesEnabled,
          lang,
          subLang
        );
      } else {
        chatCommunicationController.close();
      }
    }
  }, [
    chatWS,
    JSESSIONID,
    userId,
    tableId,
    casinoId,
    tableName,
    showOtherMessages,
    subtitlesEnabled,
    lang,
    subLang,
    isChatEnabled,
  ]);

  useEffect(() => {
    chatCommunicationController.updateConnectionOnGamePause(isGamePaused);
  }, [isGamePaused]);

  useEffect(() => {
    if (isGameAbandoned) {
      chatCommunicationController.close();
    }
  }, [isGameAbandoned]);

  return null;
};
